// COPIED from ptc-core web/src/api/feedback.ts @ de39fff (fleet feedback widget contract v1,
// 2026-10-21: ptc-docs/coordination/plans/2026-10-21-fleet-feedback-widget-contract.md §2-3).
// Changes from the source: posts to the CRM API's ABSOLUTE url with a dedicated MSAL scope
// (`crmFeedbackRequest` in config/authConfig.ts) rather than this app's own `services/api.ts`
// client, which is scoped to OUR api's audience, not the CRM's; the `app` literal is
// 'tax-lifecycle'; `ApiValidationError` has no equivalent in this app, so `FeedbackValidationError`
// is defined here, scoped to this one endpoint's error shape. Only the create half is ported --
// the admin list (`useFeedbackList`) belongs to ptc-core's own admin page, not this app.
// Conformance: FeedbackWidgetConformance.test.tsx.
//
// POST https://ptc-core-crm-api.azurewebsites.net/api/v1/feedback — any signed-in staff member.
// 201 means the report is PERSISTED; filing it as a GitHub issue is a separate, later step on the
// CRM side. There is deliberately no reporter field in the body: the server takes identity from
// the token.

import { useMutation } from '@tanstack/react-query';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '../main';
import { crmFeedbackRequest } from '../config/authConfig';
import type { FeedbackContext } from '../feedback/feedbackContext';

export type FeedbackKind = 'problem' | 'improvement';

/** Fixed by the fleet contract (§2) -- not an env var, because it never varies by environment. */
export const CRM_FEEDBACK_ENDPOINT = 'https://ptc-core-crm-api.azurewebsites.net/api/v1/feedback';

/** Exactly the id this app reports as, per the contract's §3 grant table. */
export const FEEDBACK_APP_ID = 'tax-lifecycle';

export interface CreateFeedbackBody {
  kind: FeedbackKind;
  text: string;
  expected?: string;
  creditByName: boolean;
  app: typeof FEEDBACK_APP_ID;
  context: FeedbackContext;
}

export interface FeedbackCreated {
  id: string;
  /** `FB-1234` -- what the reporter is shown. Shown verbatim; never invented locally. */
  reference: string;
  status: string;
  createdAt: string;
}

type FeedbackErrorField = 'kind' | 'app' | 'text' | 'expected';

/** A 400 "validation problem" body: `{ errors: { kind?, app?, text?, expected? } }`. */
export class FeedbackValidationError extends Error {
  constructor(public readonly errors: Partial<Record<FeedbackErrorField, string[]>>) {
    super('Feedback validation failed');
    this.name = 'FeedbackValidationError';
  }

  firstFor(field: FeedbackErrorField): string | undefined {
    return this.errors[field]?.[0];
  }
}

async function getCrmFeedbackToken(): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error('Not signed in');

  try {
    const response = await msalInstance.acquireTokenSilent({ ...crmFeedbackRequest, account });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Deliberately not acquireTokenRedirect here: a feedback send is a background action from
      // a dialog, not a navigation the reporter asked for. Surface the error; let them retry.
      throw new Error('Signing in again is required to send feedback');
    }
    throw error;
  }
}

export async function createFeedback(body: CreateFeedbackBody): Promise<FeedbackCreated> {
  const token = await getCrmFeedbackToken();

  const response = await fetch(CRM_FEEDBACK_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (response.status === 201) {
    return (await response.json()) as FeedbackCreated;
  }

  if (response.status === 400) {
    const payload = await response.json().catch(() => null);
    if (payload?.errors) throw new FeedbackValidationError(payload.errors);
    throw new Error('invalid_json');
  }

  // 401 (no token / wrong audience) and anything else: a plain error, deliberately with no
  // reference anywhere on this path (contract §5, case C9).
  throw new Error(`Feedback request failed (${response.status})`);
}

export function useCreateFeedback() {
  return useMutation({
    mutationFn: createFeedback,
  });
}
